import React from 'react';
import { Link } from 'react-router-dom';

const PomogrowPrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            Pomogrow by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: 18 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Pomogrow is a gamified Pomodoro focus timer that helps you stay productive by growing
              virtual tomato plants during focus sessions. It is developed and operated by Wolf
              Technologies Ltd ("we", "us", "our"). We are committed to protecting your privacy and
              being transparent about the data we handle. We follow a data-minimisation approach —
              we only collect what is strictly necessary to provide and improve the Service.
            </p>
            <p>
              By using Pomogrow, you agree to the terms of this Privacy Policy. If you do not agree,
              please do not use the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <h3>Account Data (Authenticated Users Only)</h3>
            <p>
              If you choose to create an account, we collect your <strong>email address</strong>,
              used for authentication, account recovery, and essential service communications. Guest
              users can use Pomogrow without creating an account — in guest mode, all data is stored
              locally on your device and we do not collect any personal information.
            </p>
            <h3>Subscription & Purchase Data</h3>
            <ul>
              <li><strong>Purchase records</strong> — subscription tier, purchase date, expiry date, and transaction identifiers.</li>
              <li><strong>Stripe Customer ID</strong> (web subscriptions) — a tokenised identifier managed by Stripe. We never store your full card number, CVV, or billing address.</li>
              <li><strong>Apple IAP receipts</strong> (iOS subscriptions) — transaction and receipt data provided by Apple for subscription validation.</li>
            </ul>
            <h3>Usage Data</h3>
            <p>We collect anonymised, aggregated usage data to understand how the Service is used:</p>
            <ul>
              <li>Session counts and durations (aggregated).</li>
              <li>Feature usage patterns (e.g. which tomato varieties are selected).</li>
              <li>App version, device type, and operating system version.</li>
            </ul>
            <h3>Diagnostics Data</h3>
            <ul>
              <li>Crash reports and error logs.</li>
              <li>App performance metrics.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>We want to be explicit about what we do not collect:</p>
            <ul>
              <li>We do <strong>not</strong> collect your name, phone number, physical address, or date of birth.</li>
              <li>We do <strong>not</strong> collect precise or coarse location data.</li>
              <li>We do <strong>not</strong> collect contacts, photos, videos, or any files from your device.</li>
              <li>We do <strong>not</strong> collect health, fitness, or biometric data.</li>
              <li>We do <strong>not</strong> collect browsing history or data from other apps.</li>
              <li>We do <strong>not</strong> use tracking or advertising identifiers.</li>
              <li>We do <strong>not</strong> sell, rent, or trade your data to any third party.</li>
              <li>We do <strong>not</strong> use your data for advertising or marketing profiling.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <ul>
              <li><strong>Provide and operate the Service</strong> — using account data and purchase data.</li>
              <li><strong>Authenticate users and manage sessions</strong> — using your email address.</li>
              <li><strong>Process and validate subscriptions</strong> — using purchase records, Stripe Customer ID, and Apple IAP receipts.</li>
              <li><strong>Sync focus session data across devices</strong> — using account data and session records.</li>
              <li><strong>Improve the Service</strong> — using anonymised usage data.</li>
              <li><strong>Diagnose and fix bugs</strong> — using diagnostics data.</li>
              <li><strong>Respond to support requests</strong> — using your email address.</li>
            </ul>
            <p>We do <strong>not</strong> use your data for any purpose other than those listed above.</p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <p>
              All server-side data is stored and processed within <strong>Amazon Web Services (AWS)
              EU West 2 (London) region</strong>. Your data remains within the United Kingdom and is
              subject to UK data protection laws.
            </p>
            <ul>
              <li><strong>Encryption in transit</strong> — all data transmitted between your device and our servers is encrypted using TLS 1.2 or higher.</li>
              <li><strong>Encryption at rest</strong> — all data stored in our databases is encrypted using AWS-managed encryption keys (AES-256).</li>
              <li><strong>Access control</strong> — server-side data is accessible only through authenticated and authorised API requests.</li>
              <li><strong>Infrastructure security</strong> — we rely on AWS's security infrastructure, which maintains compliance with ISO 27001, SOC 2, and other industry standards.</li>
            </ul>
            <p>
              Guest-mode data is stored locally on your device using IndexedDB and is not transmitted
              to our servers.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>We use the following third-party services to operate Pomogrow:</p>
            <ul>
              <li><strong>Amazon Cognito</strong> — user authentication and account management. Receives your email address.</li>
              <li><strong>Amazon DynamoDB</strong> — cloud storage of user session data and subscription records, stored in the EU West 2 (London) region.</li>
              <li><strong>Stripe</strong> — payment processing for web-based subscriptions. Receives email address and payment information (handled directly by Stripe — we never see or store your full card details).</li>
              <li><strong>Apple In-App Purchases</strong> — payment processing for iOS subscriptions. Transaction and receipt data is managed by Apple.</li>
            </ul>
            <p>We do not share your data with any other third parties.</p>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <p>
              We do <strong>not</strong> sell, rent, lease, or trade your personal data to any third
              party. We do not share your data with advertisers, data brokers, or analytics companies.
            </p>
            <p>
              Data is only shared with the third-party service providers listed above, and only to
              the extent necessary to operate the Service. We may disclose data if required to do so
              by law, regulation, legal process, or governmental request.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <ul>
              <li><strong>Account data</strong> — retained for as long as your account is active. If you delete your account, all associated data is permanently deleted within 30 days.</li>
              <li><strong>Subscription data</strong> — purchase records are retained for the duration of your subscription plus 12 months for legal and accounting purposes, after which they are deleted.</li>
              <li><strong>Usage and diagnostics data</strong> — anonymised and aggregated data may be retained indefinitely as it cannot be linked back to individual users.</li>
              <li><strong>Guest-mode data</strong> — stored locally on your device only. You can delete it at any time by clearing the app's data or uninstalling the app.</li>
            </ul>
            <p>You can request deletion by going to Settings {'>'} Account {'>'} Delete Account in the app, or by emailing us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights (GDPR)</h2>
            <p>
              Under the UK GDPR and EU GDPR, you have the following rights:
            </p>
            <ul>
              <li><strong>Access</strong> — you can request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — you can request correction of inaccurate personal data.</li>
              <li><strong>Erasure</strong> — you can request deletion of your personal data ("right to be forgotten").</li>
              <li><strong>Restriction</strong> — you can request that we limit how we use your data.</li>
              <li><strong>Data portability</strong> — you can request a machine-readable copy of your data.</li>
              <li><strong>Objection</strong> — you can object to our processing of your data.</li>
              <li><strong>Withdraw consent</strong> — where processing is based on consent, you can withdraw it at any time.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>. We will
              respond within 30 days.
            </p>
            <p>
              If you believe we have not adequately addressed your concerns, you have the right to
              lodge a complaint with the Information Commissioner's Office (ICO) at https://ico.org.uk.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Children's Privacy</h2>
            <p>
              Pomogrow is rated 4+ and does not knowingly collect personal data from children under
              13 (or under 16 in the UK/EEA where applicable). If you believe a child has provided
              us with personal data, please contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a> and we
              will promptly delete it.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will update the
              "Last Updated" date at the top of this page and notify users of material changes via an
              in-app notification or email.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Contact</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or your
              personal data, please contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
            <p>We aim to respond to all enquiries within 48 hours.</p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PomogrowPrivacy;
